import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { Source } from "trader.ts/types/source";
import { dyn, dynClass } from "trader.ts/ui";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  enrollment_status,
  lessongroupid,
  lessonid,
  studentid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeClientsideRouter } from "../../client/clientsiderouter";
import { dateFormatFuncddMMMMyyyy, dateFormatWithYear } from "../../lib/time";
import * as routes from "../../routes";
import { translateWeekday } from "../../translations_specific";
import * as noteswidget from "./noteswidget";
import { filenote, textnote } from "./noteswidget";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { HHmm } from "../../render";

type mode =
  | { tag: "none" }
  | { tag: "loading" }
  | {
      tag: "selected";
      lessongroupid: lessongroupid;
    };

const modeC = routes.magicCodec<mode>();

const modeRoute = makeRoute("/mode/{mode:mode}", {
  mode: makeEncoderFromCodec(modeC),
});
const router = makeClientsideRouter(modeRoute, { tag: "none" });

export type notesClientLg = {
  id: lessongroupid;
  weekday: joda.DayOfWeek;
  time: joda.LocalTime;
  description: string;
  unread_notes_count: number;
  students:
    | { tag: "invisible_for_student" }
    | {
        tag: "admin_or_teacher";
        students: {
          id: studentid;
          firstname: string;
          lastname: string;
          enrollment_status: enrollment_status;
        }[];
      };
  // unreadCountS: Source<number>;
};

export function render(opts: {
  schoolname: string;
  preselect: boolean;
  lessongroups_: notesClientLg[];
  opts: { today: joda.LocalDate; userIsStudent: boolean };
}): HTMLElement | HTMLElement[] {
  const modeS = router.start();
  const lessongroups = opts.lessongroups_.map((lg) => ({
    ...lg,
    unreadCountS: new Source(lg.unread_notes_count),
  }));

  const initiallySelected = opts.preselect ? lessongroups[0] : null;

  if (initiallySelected) {
    selectLg(initiallySelected);
  }

  function selectLg(
    lg: notesClientLg & { unreadCountS: Source<number> },

    force?: true,
  ) {
    lg.unreadCountS.set(0);
    document.location.hash =
      router.makeHashLink({
        tag: "selected",
        lessongroupid: lg.id,
      }) + (force === true ? "?force=" + Math.floor(Math.random() * 1000) : "");
  }

  return dynClass(
    modeS,
    (mode) =>
      mode.tag === "none" ? "" : style.listleftlayout_main_with_selection,
    <div className={style.listleftlayout_main_}>
      <aside className={style.listleftlayout_left}>
        <div className={style.listleftlayout_left_container}>
          {lessongroups.map((lg) =>
            dynClass(
              modeS,
              (mode) =>
                mode.tag === "selected" && mode.lessongroupid === lg.id
                  ? style.listleftlayout_left_item_active
                  : "",
              <div
                className={style.listleftlayout_left_item}
                onclick={() => selectLg(lg)}
              >
                <i className={FA.icon_sticky_note}></i>
                <div>
                  <div>{lg.description}</div>
                  <div style="font-size: 14px; color: grey">
                    {"%weekday om %time"
                      .replace("%weekday", translateWeekday(lg.weekday))
                      .replace("%time", lg.time.format(HHmm))}
                  </div>
                </div>
                {dyn(lg.unreadCountS, (unreadCount) =>
                  unreadCount > 0 ? (
                    <div className={style.listleftlayout_unread_badge}>
                      {unreadCount}
                    </div>
                  ) : (
                    <span></span>
                  ),
                )}
              </div>,
            ),
          )}
        </div>
      </aside>
      <div className={style.listleftlayout_middle}>
        <div className={style.listleftlayout_middle_max_width}>
          {dyn(modeS, (mode) => {
            if (mode.tag === "none") {
              return <span></span>;
            } else if (mode.tag === "loading") {
              return <div className={style.loading}></div>;
            } else if (mode.tag === "selected") {
              const lg = lessongroups.find(
                (lg) => lg.id === mode.lessongroupid,
              );
              if (!lg) {
                return <div></div>;
              }
              const notesS: Source<null | GetReturnTypeFromApiSpec<
                typeof routes.getNotesForLessongroupSpec
              >> = new Source(null);
              void rpc(routes.getNotesForLessongroupSpec, undefined, {
                lessongroupid: mode.lessongroupid,
              }).then((n) => {
                notesS.set(n);
                document
                  .querySelector("." + style.timeline_emphasis)
                  ?.scrollIntoView();
              });
              return dyn(notesS, function (notes) {
                if (notes === null) {
                  return <div className={style.loading}></div>;
                } else {
                  return (
                    <div>
                      <div>
                        <a
                          onclick={() => history.back()}
                          className={style.listleftlayout_back_link}
                        >
                          &#60; {"Terug"}
                        </a>
                      </div>
                      {renderLessongroupNotes(
                        opts.schoolname,
                        notes,
                        lg,
                        opts.opts,
                        () => selectLg(lg, true),
                      )}
                    </div>
                  );
                }
              });
            } else {
              return checkAllCasesHandled(mode);
            }
          })}
        </div>
      </div>
    </div>,
  );
}

function renderLessongroupNotes(
  schoolname: string,
  notes: {
    textnotes: textnote[];
    filenotes: filenote[];
    lessons: {
      id: lessonid;
      date: joda.LocalDate;
    }[];
    previousLgForInstrument: {
      id: lessongroupid;
      firstlesson: joda.LocalDate;
      lastlesson: joda.LocalDate;
    } | null;
  },
  lg: notesClientLg,
  opts: { today: joda.LocalDate; userIsStudent: boolean },
  reload: () => void,
): HTMLElement | HTMLElement[] {
  const ordered = notes.lessons.orderBy((l) => l.date.toEpochDay(), "asc");

  const dateToEmphasize =
    ordered.find((l) => l.date.isAfter(opts.today))?.date || opts.today;

  const isNew = false;

  const renderedLessons = notes.lessons
    .orderBy((l) => l.date.toEpochDay())
    .map((l, i) => {
      return renderTimeLinePart({
        outline: false,
        emphasize: l.date.isEqual(dateToEmphasize),
        extraStyle: isNew ? "border: 1px solid #94bbf8" : "",
        num: i + 1,
        contents: [
          <div>{dateFormatFuncddMMMMyyyy(l.date)}</div>,
          <div>
            {noteswidget.renderPerLesson(
              schoolname,
              l.id,
              notes,
              {
                id: lg.id,
                students:
                  lg.students.tag === "invisible_for_student"
                    ? []
                    : lg.students.students,
              },
              {
                smallbuttons: true,
                reload,
                fullWidthButtons: false,
                userIsStudent: opts.userIsStudent,
              },
            )}
          </div>,
        ],
      });
    });

  const previousLgRendered =
    notes.previousLgForInstrument === null ? (
      <span></span>
    ) : (
      renderTimeLinePart({
        outline: false,
        emphasize: false,
        num: 0,
        contents: [
          <div>
            {"Vorige inschrijving: %from - %until"
              .replace(
                "%from",
                notes.previousLgForInstrument.firstlesson.format(
                  dateFormatWithYear,
                ),
              )
              .replace(
                "%until",
                notes.previousLgForInstrument.lastlesson.format(
                  dateFormatWithYear,
                ),
              )}
          </div>,
          <div style="text-align: right">
            <a
              href={routes.notesRoute.link({
                selected: { lessongroup: notes.previousLgForInstrument.id },
              })}
              className={style.button_small}
              style="background-color: var(--grey-300)"
            >
              <i className={FA.icon_sticky_note}></i>
              {"Bekijk notities"}
            </a>
          </div>,
        ],
        extraStyle: "",
      })
    );

  return (
    <div>
      {previousLgRendered}
      {renderedLessons}
    </div>
  );
}

export function renderTimeLinePart(opts: {
  outline: boolean;
  emphasize: boolean;
  extraStyle: string;
  num: number | HTMLElement | null;
  contents: HTMLElement | HTMLElement[];
}): HTMLElement {
  const timelineNumberClass =
    style.timeline_number +
    (opts.outline ? style.timeline_number_outline : "") +
    (opts.emphasize ? style.timeline_emphasis : "");

  return (
    <div className={style.timeline_item} style={opts.extraStyle}>
      <div className={style.timeline_left}>
        <div className={timelineNumberClass}>{opts.num ?? ""}</div>
        <div className={style.timeline_line}></div>
      </div>
      <div className={style.timeline_content}>{opts.contents}</div>
    </div>
  );
}
